import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { trpc } from '../lib/api';
import {
  Button,
  Card,
  Space,
  Input,
  Select,
  Tag,
  Typography,
  message,
  Row,
  Col,
  Statistic,
  Tooltip,
  theme,
} from 'antd';
import {
  PlusOutlined,
  SearchOutlined,
  WarningOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  SettingOutlined,
  CopyOutlined,
  AppstoreOutlined,
  ShoppingCartOutlined,
  BarChartOutlined,
  HistoryOutlined,
  QrcodeOutlined,
} from '@ant-design/icons';
import { ResponsiveTable, type ResponsiveColumnType } from '../components/common/responsive';
import { PartFormModal } from '../components/parts/PartFormModal';
import { NameLink, ListActionsDropdown, PageHeader, PageContainer } from '../components/common';
import { CategoryTag } from '../components/common/CategoryTag';
import { CategoryTagCloudFilter } from '../components/common/CategoryTagCloudFilter';
import { LocationDisplay } from '../components/common/LocationDisplay';
import { type CategoryLike } from '../utils/categoryDisplay';
import { PermissionButton } from '../components/common/PermissionButton';
import { featureFlags } from '../utils/featureFlags';
import {
  DEFAULT_PART_LIST_SORT,
  PART_LIST_SORT_STORAGE_KEY,
  getSortOrderLabel,
  loadSortPreference,
  persistSortPreference,
  type PartListSortField,
  type SortPreference,
} from './utils/sortPreferences';

const { Title, Text } = Typography;
const { useToken } = theme;

interface SupplierSummary {
  id: string;
  name: string;
  phone?: string | null;
  email?: string | null;
}

interface Part {
  id: string;
  companyId: string;
  displayId: string;
  name: string;
  nameKana?: string | null;
  partNumber?: string | null;
  serialNumber?: string | null;
  specifications?: string | null;
  manufacturerSupplierId?: string | null;
  manufacturerSupplier?: SupplierSummary | null;
  supplierId?: string | null;
  purchaseUrl?: string | null;
  unitPrice?: string | null;
  purchaseLotSize?: number | null;
  purchaseOverhead?: string | null;
  unit?: string | null;
  locationId?: string | null;
  location?: {
    id: string;
    name: string;
    path?: string | null;
    branch?: {
      id: string;
      name: string;
    } | null;
  } | null;
  storageLocation?: string | null;
  optimalStockLevel?: number | null;
  currentStockLevel?: number | null;
  lastInventoryCount?: number | null;
  lastInventoryDate?: string | null;
  category?: CategoryLike;
  managerId?: string | null;
  notes?: string | null;
  createdBy: string;
  updatedBy?: string | null;
  deletedAt?: string | null;
  deletedBy?: string | null;
  deletionReason?: string | null;
  createdAt: string;
  updatedAt: string;
}

export function PartsPage() {
  const navigate = useNavigate();
  const { token } = useToken();
  const [searchParams] = useSearchParams();
  const [searchText, setSearchText] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | undefined>(
    searchParams.get('category') || undefined
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(20);
  const [sortPreference, setSortPreference] = useState<SortPreference<PartListSortField>>(() =>
    loadSortPreference(
      PART_LIST_SORT_STORAGE_KEY,
      ['displayId', 'name', 'stockStatus'] as const,
      DEFAULT_PART_LIST_SORT,
    )
  );
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingPart, setEditingPart] = useState<Part | null>(null);
  const [messageApi, messageContextHolder] = message.useMessage();
  const orderParam = searchParams.get('order');
  const categoryParam = searchParams.get('category') || undefined;
  const isLegacyCategoryOrderUrl = orderParam === 'category' && !!categoryParam;

  useEffect(() => {
    if (!isLegacyCategoryOrderUrl || !categoryParam) return;
    navigate(`/app/purchase-orders/category-orders/${encodeURIComponent(categoryParam)}`, {
      replace: true,
    });
  }, [categoryParam, isLegacyCategoryOrderUrl, navigate]);

  useEffect(() => {
    persistSortPreference(PART_LIST_SORT_STORAGE_KEY, sortPreference);
  }, [sortPreference]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchText, selectedCategory, sortPreference]);

  // 複製mutation
  const duplicateMutation = trpc.parts.duplicate.useMutation({
    onSuccess: (data) => {
      messageApi.success(`部品・消耗品を複製しました: ${data.name}`);
      refetch();
      // 複製した部品・消耗品の詳細ページへ遷移
      navigate(`/app/parts/${data.id}`);
    },
    onError: (error) => {
      messageApi.error(`複製に失敗しました: ${error.message}`);
    },
  });

  // 部品・消耗品一覧取得
  const { data: partsData, isLoading, refetch } = trpc.parts.list.useQuery({
    page: currentPage,
    limit: pageSize,
    search: searchText || undefined,
    category: selectedCategory && selectedCategory.trim() !== '' ? selectedCategory : undefined,
    sort: sortPreference,
  });

  // 部品・消耗品カテゴリ一覧取得（カテゴリ管理ページで管理されているもの）
  const { data: partCategories = [] } = trpc.partCategory.list.useQuery({
    includeInactive: false,
    sortBy: 'display_order',
    sortOrder: 'asc',
  });

  // カテゴリマップ作成（ID → 表示名）
  const categoryMap = useMemo(() => {
    const map: Record<string, string> = {};
    partCategories.forEach(cat => {
      map[cat.id] = cat.categoryLabel;
    });
    return map;
  }, [partCategories]);

  const parts = useMemo<Part[]>(
    () => (Array.isArray(partsData?.items) ? (partsData.items as unknown as Part[]) : []),
    [partsData]
  );
  const total = partsData?.pagination?.total || 0;

  // 在庫状況タグ
  const getStockLevelTag = (current: number | null | undefined, optimal: number | null | undefined) => {
    const currentStock = current ?? 0;
    const optimalStock = optimal ?? 0;

    if (currentStock === 0) {
      return <Tag icon={<ExclamationCircleOutlined />} color="error">在庫切れ</Tag>;
    }
    if (currentStock < optimalStock) {
      return <Tag icon={<WarningOutlined />} color="warning">在庫不足</Tag>;
    }
    return <Tag icon={<CheckCircleOutlined />} color="success">在庫あり</Tag>;
  };

  // 削除確認
  // 編集モーダル表示
  const handleEdit = (part: Part) => {
    setEditingPart(part);
    setIsModalVisible(true);
  };

  // 新規登録モーダル表示
  const handleCreate = () => {
    setEditingPart(null);
    setIsModalVisible(true);
  };

  // モーダル閉じる
  const handleModalClose = () => {
    setIsModalVisible(false);
    setEditingPart(null);
  };

  // モーダル成功
  const handleModalSuccess = () => {
    refetch();
    handleModalClose();
  };

  // 統計データ計算
  const totalParts = total;
  const lowStockParts = parts.filter(p => (p.currentStockLevel ?? 0) < (p.optimalStockLevel ?? 0)).length;
  const outOfStockParts = parts.filter(p => (p.currentStockLevel ?? 0) === 0).length;

  // テーブルカラム定義
  const columns: ResponsiveColumnType<Part>[] = [
    {
      title: `部品・消耗品名`,
      dataIndex: 'name',
      key: 'name',
      width: 200,
      fixed: 'left',
      mobileRole: 'title',
      mobilePriority: 1,
      render: (text, record) => (
        <NameLink
          name={text}
          onClick={() => navigate(`/app/parts/${record.id}`)}
        />
      ),
    },
    {
      title: '型式',
      dataIndex: 'partNumber',
      key: 'partNumber',
      width: 150,
      mobileRole: 'extra',
      mobilePriority: 2,
    },
    {
      title: 'カテゴリ',
      dataIndex: 'category',
      key: 'category',
      width: 60,
      mobileRole: 'tag',
      mobilePriority: 3,
      render: (category: CategoryLike) => (
        <CategoryTag
          category={category}
          linkPath="/app/parts"
          categoryMap={categoryMap}
        />
      ),
    },
    {
      title: 'メーカー',
      key: 'manufacturerSupplier',
      width: 100,
      mobileRole: 'extra',
      mobilePriority: 5,
      render: (_, record) =>
        record.manufacturerSupplier ? (
          <NameLink
            name={record.manufacturerSupplier.name}
            onClick={() => navigate(`/app/suppliers/${record.manufacturerSupplier?.id}`)}
          />
        ) : (
          <Text type="secondary">-</Text>
        ),
    },
    {
      title: '在庫状況',
      key: 'stock',
      width: 80,
      mobileRole: 'extra',
      mobilePriority: 1,
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          {getStockLevelTag(record.currentStockLevel, record.optimalStockLevel)}
          <Text type="secondary" style={{ fontSize: 12 }}>
            {record.currentStockLevel ?? 0} / {record.optimalStockLevel ?? 0} {record.unit ?? '個'}
          </Text>
        </Space>
      ),
    },
    {
      title: '単価(税込)',
      dataIndex: 'unitPrice',
      key: 'unitPrice',
      width: 120,
      align: 'right',
      mobileRole: 'hidden',
      render: (price) => price ? `¥${parseFloat(price).toLocaleString()}` : '-',
    },
    {
      title: '保管エリア',
      key: 'location',
      width: 200,
      mobileRole: 'hidden',
      render: (_, record) => {
        if (record.location) {
          const displayText = record.location.branch
            ? `${record.location.branch.name} > ${record.location.name}`
            : record.location.name;
          const fullPath = record.location.path || displayText;

          return (
            <Tooltip title={fullPath}>
              <LocationDisplay
                location={record.location}
                showIcon={true}
                noLink={false}
              />
            </Tooltip>
          );
        }
        // フォールバック: 旧storageLocationを表示
        if (record.storageLocation) {
          return <Text>{record.storageLocation}</Text>;
        }
        return <Text type="secondary">-</Text>;
      },
    },
    {
      title: '操作',
      key: 'actions',
      width: 40,
      align: 'center',
      fixed: 'right',
      mobileRole: 'hidden',
      render: (_, record) => (
        <ListActionsDropdown
          onDetail={() => navigate(`/app/parts/${record.id}`)}
          onEdit={() => handleEdit(record)}
          additionalActions={[
            {
              key: 'duplicate',
              icon: <CopyOutlined />,
              label: '複製',
              onClick: () => duplicateMutation.mutate({ id: record.id }),
            },
          ]}
        />
      ),
    },
  ];

  return (
    <PageContainer>
      {messageContextHolder}

      {/* ヘッダー */}
      <PageHeader
        title="部品・消耗品管理"
        subtitle="部品・消耗品の在庫管理と発注履歴を管理できます。"
        extra={
          <Space>
            <PermissionButton
              icon={<SettingOutlined />}
              onClick={() => navigate('/app/categories#part')}
              title="部品・消耗品カテゴリ管理"
              requiredPermission="manage"
            >
              カテゴリ管理
            </PermissionButton>
            {featureFlags.qrLabelPrinting && (
              <Button
                icon={<QrcodeOutlined />}
                onClick={() => navigate('/app/qr-labels#part')}
              >
                QRコードシール印刷
              </Button>
            )}
            <PermissionButton
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleCreate}
              requiredPermission="edit"
            >
              新規部品・消耗品登録
            </PermissionButton>
          </Space>
        }
      />

      {/* 部品が0件の場合 - 登録メリットの説明バナー */}
      {!isLoading && total === 0 && (
        <Card
          style={{
            marginBottom: 24,
            background: token.colorInfoBg,
            border: `1px solid ${token.colorInfoBorder}`,
          }}
        >
          <Row align="middle" gutter={16} style={{ marginBottom: 16 }}>
            <Col>
              <AppstoreOutlined style={{ fontSize: 32, color: token.colorPrimary }} />
            </Col>
            <Col flex={1}>
              <Title level={5} style={{ marginBottom: 4 }}>
                部品・消耗品を登録しましょう
              </Title>
              <Text type="secondary">
                部品・消耗品を登録すると、以下の機能が利用できるようになります。
              </Text>
            </Col>
          </Row>
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={8}>
              <Card size="small" style={{ height: '100%', background: token.colorBgContainer }}>
                <Space>
                  <BarChartOutlined style={{ fontSize: 20, color: token.colorSuccess }} />
                  <div>
                    <Text strong>在庫管理</Text>
                    <br />
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      在庫数を把握し、不足を事前に検知
                    </Text>
                  </div>
                </Space>
              </Card>
            </Col>
            <Col xs={24} sm={8}>
              <Card size="small" style={{ height: '100%', background: token.colorBgContainer }}>
                <Space>
                  <ShoppingCartOutlined style={{ fontSize: 20, color: token.colorWarning }} />
                  <div>
                    <Text strong>発注管理</Text>
                    <br />
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      発注履歴を記録し、コストを管理
                    </Text>
                  </div>
                </Space>
              </Card>
            </Col>
            <Col xs={24} sm={8}>
              <Card size="small" style={{ height: '100%', background: token.colorBgContainer }}>
                <Space>
                  <HistoryOutlined style={{ fontSize: 20, color: '#722ed1' }} />
                  <div>
                    <Text strong>消費管理</Text>
                    <br />
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      使用履歴を追跡し、消耗パターンを分析
                    </Text>
                  </div>
                </Space>
              </Card>
            </Col>
          </Row>
        </Card>
      )}

      {/* 統計カード */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={8}>
          <Card>
            <Statistic
              title={`登録部品・消耗品数`}
              value={totalParts}
              suffix="件"
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card>
            <Statistic
              title="在庫不足"
              value={lowStockParts}
              suffix="件"
              valueStyle={{ color: lowStockParts > 0 ? '#faad14' : undefined }}
              prefix={lowStockParts > 0 ? <WarningOutlined /> : undefined}
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card>
            <Statistic
              title="在庫切れ"
              value={outOfStockParts}
              suffix="件"
              valueStyle={{ color: outOfStockParts > 0 ? '#ff4d4f' : undefined }}
              prefix={outOfStockParts > 0 ? <ExclamationCircleOutlined /> : undefined}
            />
          </Card>
        </Col>
      </Row>

      {/* 検索・フィルタ */}
      <Card style={{ marginBottom: 16 }}>
        <Row gutter={[12, 12]} align="middle" style={{ marginBottom: 16 }}>
          <Col xs={24} sm={12} md={10}>
            <Input
              placeholder={`部品・消耗品名、型式で検索`}
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Select
              style={{ width: '100%' }}
              value={sortPreference.field}
              onChange={(value) =>
                setSortPreference((current) => ({
                  ...current,
                  field: value as PartListSortField,
                }))
              }
              data-testid="parts-sort-field-select"
            >
              <Select.Option value="displayId">表示ID</Select.Option>
              <Select.Option value="name">部品名</Select.Option>
              <Select.Option value="stockStatus">在庫状況</Select.Option>
            </Select>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Select
              style={{ width: '100%' }}
              value={sortPreference.order}
              onChange={(value) =>
                setSortPreference((current) => ({
                  ...current,
                  order: value as SortPreference<PartListSortField>['order'],
                }))
              }
              data-testid="parts-sort-order-select"
            >
              <Select.Option value="asc">{getSortOrderLabel(sortPreference.field, 'asc')}</Select.Option>
              <Select.Option value="desc">{getSortOrderLabel(sortPreference.field, 'desc')}</Select.Option>
            </Select>
          </Col>
        </Row>

        {/* カテゴリタグクラウドフィルタ */}
        <CategoryTagCloudFilter
          categories={partCategories.map(cat => ({
            id: cat.id,
            categoryLabel: cat.categoryLabel,
            color: cat.color,
            icon: cat.icon,
            count: parts?.filter((p) => {
              if (!p.category || typeof p.category === 'string') {
                return p.category === cat.id;
              }
              return p.category.id === cat.id;
            }).length || 0,
          }))}
          selectedCategory={selectedCategory}
          onCategoryChange={(categoryId) => setSelectedCategory(categoryId)}
          loading={!partCategories.length && isLoading}
          showCounts={true}
          maxVisibleTags={15}
        />
      </Card>

      {/* 部品・消耗品一覧テーブル */}
      <Card>
        <ResponsiveTable<Part>
          columns={columns}
          dataSource={parts}
          loading={isLoading}
          rowKey="id"
          scrollX={1400}
          mobileCardConfig={{
            titleField: (record) => (
              <a
                onClick={() => navigate(`/app/parts/${record.id}`)}
                style={{ color: '#1890ff', fontWeight: 'bold', cursor: 'pointer' }}
              >
                {record.name}
              </a>
            ),
            actions: (record) => (
              <ListActionsDropdown
                onDetail={() => navigate(`/app/parts/${record.id}`)}
                onEdit={() => handleEdit(record)}
                additionalActions={[
                  {
                    key: 'duplicate',
                    icon: <CopyOutlined />,
                    label: '複製',
                    onClick: () => duplicateMutation.mutate({ id: record.id }),
                  },
                ]}
              />
            ),
            onClick: (record) => navigate(`/app/parts/${record.id}`),
          }}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: total,
            showSizeChanger: false,
            showTotal: (total) => `全 ${total} 件`,
            onChange: (page) => setCurrentPage(page),
          }}
        />
      </Card>

      {/* 部品・消耗品フォームモーダル */}
      <PartFormModal
        open={isModalVisible}
        part={editingPart}
        onClose={handleModalClose}
        onSuccess={handleModalSuccess}
      />
    </PageContainer>
  );
}
